import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import CurrencyInput from '@/components/ui/CurrencyInput';
import { useFormDraft } from '@/hooks/useFormDraft';

export default function AddSplitModal({ open, onClose }) {
  const { t } = useTranslation();
  const addSplit = useStore((s) => s.addSplit);
  const draft = useFormDraft('addSplit');

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [splitType, setSplitType] = useState('equal');
  const [myPercentage, setMyPercentage] = useState('50');
  const [errors, setErrors] = useState({});
  const [loaded, setLoaded] = useState(false);

  // Load draft on open
  if (open && !loaded) {
    const saved = draft.load();
    if (saved) {
      setDescription(saved.description || '');
      setAmount(saved.amount || '');
      setSplitType(saved.splitType || 'equal');
      setMyPercentage(saved.myPercentage || '50');
    }
    setLoaded(true);
  }
  if (!open && loaded) setLoaded(false);

  // Auto-save draft
  if (open && loaded) {
    draft.save({ description, amount, splitType, myPercentage });
  }

  const validate = () => {
    const errs = {};
    if (!description.trim()) errs.description = t('couple.split.descriptionRequired');
    if (!amount || Number(amount) <= 0) errs.amount = t('couple.split.amountRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const total = Number(amount);
    let myShare, partnerShare;

    if (splitType === 'equal') {
      myShare = total / 2;
      partnerShare = total / 2;
    } else {
      const pct = Number(myPercentage) / 100;
      myShare = Math.round(total * pct * 100) / 100;
      partnerShare = total - myShare;
    }

    addSplit({
      description: description.trim(),
      amount: total,
      myShare,
      partnerShare,
      date: new Date().toISOString().split('T')[0],
    });
    draft.clear();
    handleClose();
  };

  const handleClose = () => {
    setDescription('');
    setAmount('');
    setSplitType('equal');
    setMyPercentage('50');
    setErrors({});
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={t('couple.split.addNew')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('transactions.form.description')}
          </label>
          <input
            type="text"
            className={`input ${errors.description ? 'border-danger-500' : ''}`}
            placeholder={t('couple.split.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {errors.description && <p className="text-xs text-danger-500 mt-1">{errors.description}</p>}
        </div>

        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('transactions.form.amount')}
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-300">$</span>
            <CurrencyInput
              placeholder="0"
              value={amount}
              onChange={setAmount}
              className={`w-full pl-10 pr-4 py-3 text-lg font-bold input ${errors.amount ? 'border-danger-500' : ''}`}
            />
          </div>
          {errors.amount && <p className="text-xs text-danger-500 mt-1">{errors.amount}</p>}
        </div>

        {/* Split type */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('couple.split.type')}
          </label>
          <div className="flex gap-2">
            {['equal', 'percentage', 'custom'].map((st) => (
              <button
                key={st}
                onClick={() => setSplitType(st)}
                className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-all ${
                  splitType === st
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                {t(`couple.split.${st}`)}
              </button>
            ))}
          </div>
        </div>

        {splitType !== 'equal' && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('couple.split.myShare')} (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={myPercentage}
              onChange={(e) => setMyPercentage(e.target.value)}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('couple.split.partnerGets')}: {100 - Number(myPercentage)}%
            </p>
          </div>
        )}

      </div>
    </Modal>
  );
}
